import moment from "moment";
import styled from "styled-components";
import { useFlexLayout } from "react-table";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import React, { useEffect, useContext, useMemo } from "react";
import {
  TableWitPagination,
  SortHeader,
  PaginationContext,
  SortContext,
  SearchContext,
  DashPlaceholder,
  Image,
  Flex,
  DropDown,
  Icon,
  ModalContext,
} from "dash-ui";
import { DATE_FORMAT, FileModel } from "dash-common";

import {
  OrganizationFragment,
  useGetOrganizationsForTableQuery,
} from "gql/generated.graphql";
import { RoutesPath } from "flow-components/routes/routes";
import DeliveryImage from "assets/images/png/Delivery.png";
import SearchingImage from "assets/images/png/Searching.png";
import { OrganizationModals } from "pages/organizations/constants/constants";

const NameColumnFlex = styled(Flex)`
  cursor: pointer;
`;

function OrganizationsTable() {
  const { t } = useTranslation("tables", { keyPrefix: "organizations" });
  const { t: tLabels } = useTranslation("common", { keyPrefix: "labels" });
  const { t: tPlaceholders } = useTranslation("common", {
    keyPrefix: "placeholders",
  });
  const navigate = useNavigate();
  const { toggleModal, setPayload } = useContext(ModalContext);
  const {
    setTotal,
    state: paginationState,
    getRefetchProps,
  } = useContext(PaginationContext);
  const { searchSlug } = useContext(SearchContext);
  const { state: sort } = useContext(SortContext);
  const { skip, limit } = getRefetchProps(paginationState);
  const searchByName = searchSlug.length > 2 ? searchSlug : null;
  // todo: add load state to table
  const {
    data: result,
    refetch,
    loading,
  } = useGetOrganizationsForTableQuery({
    variables: {
      sort: [sort],
      pagination: { skip, limit },
      searchByName,
    },
  });
  const { data, total } = useMemo(
    () => ({
      data: result?.getOrganizations?.collection || [],
      total: result?.getOrganizations?.totalCount || 0,
    }),
    [result]
  );

  const columns = React.useMemo(
    () => [
      {
        Header: () => (
          <SortHeader accessor="name" isDefault>
            {t`organizationNameLabel`}
          </SortHeader>
        ),
        accessor: "name",
        Cell: ({
          value,
          row,
        }: {
          value: string;
          row: { original: { logoFile?: FileModel; _id: string } };
        }) => (
          <NameColumnFlex
            alignItems="center"
            onClick={() =>
              navigate(
                RoutesPath.OrganizationDetails.replace(":id", row.original._id)
              )
            }
          >
            {row?.original?.logoFile ? (
              <Image
                mr={2}
                src={row.original.logoFile.publicUrl}
                fit="contain"
              />
            ) : (
              <DashPlaceholder mr={2} />
            )}
            {value}
          </NameColumnFlex>
        ),
      },
      {
        Header: () => (
          <SortHeader accessor="propertiesCount">{t`totalPropertiesLabel`}</SortHeader>
        ),
        accessor: "propertiesCount",
        width: 50,
      },
      {
        Header: () => (
          <SortHeader accessor="createdAt">{t`addedOnLabel`}</SortHeader>
        ),
        accessor: "createdAt",
        Cell: ({ value }: { value: Date }) => moment(value).format(DATE_FORMAT),
        width: 50,
      },
      {
        Header: () => tLabels`actions`,
        accessor: "_id",
        width: 20,
        Cell: ({
          row: { original },
        }: {
          row: { original: OrganizationFragment };
        }) => (
          <DropDown
            placementHorizontal="left"
            Anchor={() => <Icon name="dots" size={20} />}
            items={[
              {
                children: tLabels`delete`,
                key: "close",
                icon: "close",
                onClick: () => {
                  setPayload(original);
                  toggleModal(true, OrganizationModals.DELETE_ORGANIZATION);
                },
              },
            ]}
          />
        ),
      },
    ],
    [t, navigate, tLabels, toggleModal, setPayload]
  );

  useEffect(() => {
    // FIXME: re-creating context functions everytime state change. Use useCallback
    setTotal(total);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total]);

  useEffect(() => {
    // FIXME: re-creating context functions everytime state change. Use useCallback
    refetch({
      sort: [sort],
      pagination: { skip, limit },
      searchByName,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip, limit, sort, searchSlug]);

  const labelToShow = !loading
    ? (searchByName
        ? tPlaceholders`noSearchResults`
        : t`thereAreNoOrganizations`) || ""
    : "";

  const imageToShow = !loading
    ? searchByName
      ? SearchingImage
      : DeliveryImage
    : undefined;

  return (
    <TableWitPagination
      w="100%"
      tableProps={{
        data,
        columns,
        loading,
        layouts: [useFlexLayout],
        maxHeight: "calc(100vh - 350px)",
        stickyHeader: true,
        placeholderProps: {
          label: labelToShow,
          image: imageToShow,
        },
      }}
      paginationProps={{
        label: t`paginationLabel`,
        previousButtonLabel: tLabels`previous`,
        nextButtonLabel: tLabels`next`,
      }}
    />
  );
}

export default OrganizationsTable;
